import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _ from 'underscore';

import { Glyphicon } from '@bingads-webui-react/primitive';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Constants } from '@bingads-webui-clientcenter/entity-utils';

import { getMonitoringCardStyles } from '../styles';
import { EstimateType, IconName, Severity } from '../constants';
import { formatCurrency, formatInteger } from '../utils';

const { RecommendationType, BudgetEventType } = Constants;

export const MonitoringCard = ({
  i18n,
  currencyCode,
  eventType,
  severity,
  message,
  hint,
  deltaEstimates,
  actions,
}) => {
  const classes = useDefaultStyles(getMonitoringCardStyles);

  const formatEstimate = useCallback(({ type, amount }) => {
    switch (type) {
      case EstimateType.Conversions: {
        const conversions = formatInteger({ i18n, value: amount });
        return i18n.getString(_TL_('{{conversions}} Conversions'), { conversions });
      }
      case EstimateType.Clicks: {
        const clicks = formatInteger({ i18n, value: amount });
        return i18n.getString(_TL_('{{clicks}} Clicks'), { clicks });
      }
      case EstimateType.Impressions: {
        const impressions = formatInteger({ i18n, value: amount });
        return i18n.getString(_TL_('{{impressions}} Impressions'), { impressions });
      }
      case EstimateType.Spend: {
        const spend = formatCurrency({
          i18n, value: amount, currencyCode, options: { withSign: 'always' },
        });
        return i18n.getString(_TL_('{{spend}} Spend'), { spend });
      }
      default:
        return null;
    }
  }, [currencyCode, i18n]);

  const messageNode = useMemo(() => (
    <div className={classnames('monitoring-card-message', classes.Message)}>
      {message}
    </div>
  ), [classes.Message, message]);

  const hintNode = useMemo(() => (
    hint && (
      <div className={classnames('monitoring-card-hint', classes.Hint)}>
        {hint}
      </div>
    )
  ), [classes.Hint, hint]);

  const estimatesNode = useMemo(() => {
    if (_.isEmpty(deltaEstimates)) {
      return null;
    }

    return (
      <div className={classnames('monitoring-card-estimates', classes.Estimates)}>
        <span className={classnames('monitoring-card-weekly-estimates-text', classes.WeeklyEstimatesText)}>
          {i18n.getString(_TL_('Weekly estimates:'))}
        </span>
        {_.map(deltaEstimates, ({ type, amount }) => (
          <span key={type} className={classnames('monitoring-card-estimate', classes.Estimate, `estimate-${type}`, classes[type])}>
            {formatEstimate({ type, amount })}
          </span>
        ))}
      </div>
    );
  }, [classes, deltaEstimates, formatEstimate, i18n]);

  const actionsNode = useMemo(() => (
    actions && (
      <div className={classnames('monitoring-card-actions', classes.Actions)}>
        {actions}
      </div>
    )
  ), [actions, classes.Actions]);

  return (
    <div className={classnames('monitoring-card-container', `monitoring-card-eventType-${eventType}`, classes.Container)}>
      <Glyphicon glyph={`ba-${IconName[severity]}`} className={classnames('monitoring-card-icon', classes.Icon, classes[`Icon-Severity-${severity}`])} />
      <div className={classnames('monitoring-card-content', classes.Content)}>
        {messageNode}
        {hintNode}
        {estimatesNode}
      </div>
      {actionsNode}
    </div>
  );
};

MonitoringCard.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  currencyCode: PropTypes.string.isRequired,
  eventType: PropTypes.oneOf([..._.values(BudgetEventType), ..._.values(RecommendationType)]).isRequired,
  severity: PropTypes.oneOf(_.values(Severity)).isRequired,
  message: PropTypes.string.isRequired,
  hint: PropTypes.string,
  deltaEstimates: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.oneOf(_.values(EstimateType)).isRequired,
    amount: PropTypes.number.isRequired,
  })),
  actions: PropTypes.node,
};

MonitoringCard.defaultProps = {
  hint: null,
  deltaEstimates: [],
  actions: null,
};
